<div class="progress-slot">
  @if (loading()) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  }
</div>
@if (editOptions()) {
  <div class="table-actions">
    <app-nnn-registration-version-select
      class="table-actions__version-select"
      [registrations]="registrationList()"
      [selectedId]="selectedVersionId()"
      [showNewOption]="registrationList().length === 0 && state() === conceptState && writeEnabled()"
      (versionChange)="onVersionChange($event)"></app-nnn-registration-version-select>
    <div class="table-actions__buttons">
      @if (rowViews().length > 0) {
        <button
          mat-icon-button
          type="button"
          class="table-actions__toggle-rows"
          (click)="toggleAllRows()"
          matTooltip="{{
            (allExpanded()
              ? 'epd.functions.careplan-nnn.row-table.collapse-all'
              : 'epd.functions.careplan-nnn.row-table.expand-all'
            )
              | translate
              | capitalize
          }}"
          matTooltipClass="card-tooltip">
          <mat-icon>{{ allExpanded() ? 'unfold_less' : 'unfold_more' }}</mat-icon>
        </button>
      }
      @if (state() === conceptState && writeEnabled()) {
        <app-spinner-button
          [config]="previousRegistrationBtnConfig"
          (onClick)="importPreviousRegistration()"
          [disabled]="!lastFinalRegistration()"></app-spinner-button>
        <app-spinner-button
          [config]="makeFinalBtnConfig"
          cdkOverlayOrigin
          #trigger="cdkOverlayOrigin"
          (onClick)="toggleConfirmFinal(trigger)"
          [disabled]="!activeRegistration()"></app-spinner-button>
      }
      @if (activeRegistration() && (state() === conceptState ? writeEnabled() : deleteEnabled())) {
        <app-spinner-button [config]="deleteBtnConfig" (onClick)="deleteRegistration()"></app-spinner-button>
      }
    </div>
  </div>
}

@if (isUserTeacher && activeRegistration() && state() === conceptState) {
  <app-nnn-view-user-fields
    [date]="viewDate()"
    [time]="viewTime()"
    [user]="viewUser()"
    (dateChange)="onViewDateChange($event)"
    (timeChange)="onViewTimeChange($event)"
    (userChange)="onViewUserChange($event)"></app-nnn-view-user-fields>
}

<mat-accordion multi class="row-cards">
  @for (view of rowViews(); track view.row.id) {
    @let row = view.row;
    <mat-expansion-panel
      class="row-card"
      hideToggle
      [expanded]="isExpanded(row.id)"
      (opened)="setRowExpanded(row.id, true)"
      (closed)="setRowExpanded(row.id, false)">
      <mat-expansion-panel-header class="row-card__header" [class.row-card__header--open]="isExpanded(row.id)">
        <div class="row-card__header-content">
          <span class="row-card__heading">
            <span class="row-card__name" [matTooltip]="view.classification" matTooltipClass="card-tooltip">
              {{ view.classification }}
            </span>
            <span class="row-card__type" [matTooltip]="view.diagnosisName" matTooltipClass="card-tooltip">
              @if (view.diagnosisCode; as code) {
                <span class="row-card__code">{{ code }}</span>
              }
              {{ view.diagnosisName }}
            </span>
          </span>
          @if (!isExpanded(row.id)) {
            <span class="row-card__summary">
              <span class="row-card__pill">
                <mat-icon inline>flag</mat-icon>
                @if (row.results.length === 0) {
                  {{ 'epd.functions.careplan-nnn.row-card.no-outcomes' | translate | capitalize }}
                } @else {
                  {{ row.results.length }}
                  {{
                    (row.results.length === 1
                      ? 'epd.functions.careplan-nnn.row-card.outcome'
                      : 'epd.functions.careplan-nnn.row-card.outcomes'
                    ) | translate
                  }}
                }
              </span>
              <span class="row-card__pill">
                <mat-icon inline>bolt</mat-icon>
                @if (row.interventions.length === 0) {
                  {{ 'epd.functions.careplan-nnn.row-card.no-interventions' | translate | capitalize }}
                } @else {
                  {{ row.interventions.length }}
                  {{
                    (row.interventions.length === 1
                      ? 'epd.functions.careplan-nnn.row-card.intervention'
                      : 'epd.functions.careplan-nnn.row-card.interventions'
                    ) | translate
                  }}
                }
              </span>
              <span class="row-card__pill">
                <mat-icon inline>event</mat-icon>
                @if (row.evaluationDate) {
                  {{ 'epd.functions.careplan-nnn.row-card.evaluation' | translate | capitalize }}
                  {{ row.evaluationDate | date: 'dd-MM-yyyy' }}
                } @else {
                  {{ 'epd.functions.careplan-nnn.row-card.no-evaluation-date' | translate | capitalize }}
                }
              </span>
            </span>
          } @else {
            <span class="row-card__spacer"></span>
          }
          <mat-icon class="row-card__chevron" [class.row-card__chevron--open]="isExpanded(row.id)"
            >expand_more</mat-icon
          >
        </div>
      </mat-expansion-panel-header>
      <ng-template matExpansionPanelContent>
        <div class="row-card__body">
          <div class="row-card__columns">
            <div class="row-card__column">
              <span class="row-card__column-title">
                {{ 'epd.functions.careplan-nnn.row-table.columns.diagnosis' | translate | capitalize }}
              </span>
              <app-nnn-diagnosis-section
                [patientId]="patientId()"
                [diagnose]="view.diagnose"
                [diagnosisName]="view.diagnosisName"
                [kenmerken]="row.kenmerken"
                [notes]="row.diagnosisNotes"
                [editable]="rowsEditable()"
                [showNotesCounter]="state() === conceptState"
                [notesSaveState]="saveState(row.id, 'diagnosisNotes')"
                [kenmerkenSaveState]="saveState(row.id, 'kenmerken')"
                (kenmerkenChange)="onKenmerkenChange(row, $event)"
                (notesInput)="onNotesInput(row, 'diagnosisNotes', $event)"
                (notesSave)="saveNotes(row, 'diagnosisNotes')"></app-nnn-diagnosis-section>
            </div>
            <div class="row-card__column">
              <span class="row-card__column-title">
                {{ 'epd.functions.careplan-nnn.row-table.columns.results' | translate | capitalize }}
              </span>
              <app-nnn-results-section
                [results]="row.results"
                [diagnoseCode]="view.diagnosisCode"
                [nocIndex]="nocIndex()"
                [editable]="rowsEditable()"
                [saveState]="saveState(row.id, 'results')"
                (resultsChange)="onResultsChange(row, $event)"></app-nnn-results-section>
            </div>
            <div class="row-card__column">
              <span class="row-card__column-title">
                {{ 'epd.functions.careplan-nnn.row-table.columns.interventions' | translate | capitalize }}
              </span>
              <app-nnn-interventions-section
                [interventions]="row.interventions"
                [diagnoseCode]="view.diagnosisCode"
                [nicIndex]="nicIndex()"
                [editable]="rowsEditable()"
                [saveState]="saveState(row.id, 'interventions')"
                (interventionsChange)="onInterventionsChange(row, $event)"></app-nnn-interventions-section>
            </div>
            <div class="row-card__column">
              <span class="row-card__column-title">
                {{ 'epd.functions.careplan-nnn.row-table.columns.instruments' | translate | capitalize }}
              </span>
              <app-nnn-instruments-section
                [instruments]="row.instruments"
                [appFunctionGroups]="appFunctionGroups()"
                [editable]="rowsEditable()"
                [saveState]="saveState(row.id, 'instruments')"
                (instrumentsChange)="onInstrumentsChange(row, $event)"></app-nnn-instruments-section>
            </div>
          </div>
          <div class="row-card__eval-row">
            <span class="row-card__column-title">
              {{ 'epd.functions.careplan-nnn.row-table.columns.evaluation-date' | translate | capitalize }}
            </span>
            <app-nnn-evaluation-date-cell
              class="row-card__eval-date"
              labelKey="epd.functions.careplan-nnn.row-table.columns.evaluation-date"
              [value]="row.evaluationDate"
              [disabled]="!rowsEditable()"
              [saveState]="saveState(row.id, 'evaluationDate')"
              (dateChange)="saveEvaluationDate(row, $event)"></app-nnn-evaluation-date-cell>
          </div>
          @if (editOptions() && state() === conceptState) {
            <div class="row-card__footer">
              <app-spinner-button
                matTooltip="{{ 'general.actions.delete' | translate | capitalize }}"
                matTooltipClass="card-tooltip"
                [config]="deleteRowBtnConfig"
                (onClick)="removeRow(row)"
                [disabled]="!writeEnabled()">
                <div class="row-card__delete-content">
                  <mat-icon>delete</mat-icon>
                  <span>{{ 'epd.functions.careplan-nnn.row-card.delete-row' | translate | capitalize }}</span>
                </div>
              </app-spinner-button>
            </div>
          }
        </div>
      </ng-template>
    </mat-expansion-panel>
  }
</mat-accordion>

<div class="add-row">
  @if (editOptions() && state() === conceptState && writeEnabled()) {
    <app-spinner-button [config]="addRowBtnConfig()" (onClick)="openAddRowModal()">
      <div class="add-row__content">
        <mat-icon>add</mat-icon>
        <span class="bold">{{ 'epd.functions.careplan.row-table.add-row' | translate | capitalize }}</span>
      </div>
    </app-spinner-button>
  }
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="triggerOrigin"
  [cdkConnectedOverlayOpen]="confirmFinalOpen()">
  <app-confirm-delete-overlay [confirmDeleteText]="confirmFinalText" (close)="onConfirmFinalClose($event)">
  </app-confirm-delete-overlay>
</ng-template>
